import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export function FinancialStatements() {
  const { selectedEntity } = useEntity();
  const { language } = useLanguage();
  const [year, setYear] = useState(new Date().getFullYear());
  const [balans, setBalans] = useState(null);
  const [wv, setWv] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatements = useCallback(async () => {
    if (!selectedEntity) return;
    setLoading(true);
    try {
      const [balRes, wvRes] = await Promise.all([
        fetch(`${API_URL}/api/balans/${selectedEntity.entity_id}?year=${year}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/winst-verlies/${selectedEntity.entity_id}?year=${year}`, { credentials: 'include' })
      ]);
      if (balRes.ok) setBalans(await balRes.json());
      if (wvRes.ok) setWv(await wvRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedEntity, year]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  const downloadPdf = async (type) => {
    const endpoint = type === 'balans'
      ? `${API_URL}/api/balans/${selectedEntity.entity_id}/pdf?year=${year}`
      : `${API_URL}/api/winst-verlies/${selectedEntity.entity_id}/pdf?year=${year}`;
    try {
      const res = await fetch(endpoint, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = type === 'balans' ? `Balans_${year}.pdf` : `WV_${year}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('PDF gedownload');
    } catch { toast.error('PDF download mislukt'); }
  };

  const sym = selectedEntity?.country === 'NL' ? '\u20AC' : '$';
  const isNL = selectedEntity?.country === 'NL';

  if (!selectedEntity) {
    return (<div className="flex items-center justify-center h-64"><p className="text-muted-foreground">{language === 'nl' ? 'Selecteer een entiteit' : 'Select an entity'}</p></div>);
  }

  return (
    <div className="space-y-6" data-testid="financial-statements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {language === 'nl' ? 'Financiele Overzichten' : 'Financial Statements'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{selectedEntity.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="year-select">
            {[2023, 2024, 2025, 2026].map(y => (<option key={y} value={y}>{y}</option>))}
          </select>
        </div>
      </div>

      {loading ? (<p className="text-center text-muted-foreground py-12">Laden...</p>) : (
        <Tabs defaultValue="balans">
          <TabsList>
            <TabsTrigger value="balans">{language === 'nl' ? 'Balans' : 'Balance Sheet'}</TabsTrigger>
            <TabsTrigger value="wv">{language === 'nl' ? 'Winst & Verlies' : 'Profit & Loss'}</TabsTrigger>
          </TabsList>
          <TabsContent value="balans">
            {balans && <>
              <div className="flex justify-end mb-2">
                <Button variant="outline" size="sm" onClick={() => downloadPdf('balans')} data-testid="download-balans-pdf">
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
              <BalanceSheet data={balans} sym={sym} language={language} isNL={isNL}
                entityId={selectedEntity.entity_id} year={year} onSaved={fetchStatements} />
            </>}
          </TabsContent>
          <TabsContent value="wv">
            {wv && <>
              <div className="flex justify-end mb-2">
                <Button variant="outline" size="sm" onClick={() => downloadPdf('wv')} data-testid="download-wv-pdf">
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
              <ProfitLoss data={wv} sym={sym} language={language} isNL={isNL} />
            </>}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function fmt(sym, val) {
  const abs = Math.abs(val).toFixed(2);
  return val < 0 ? `(${sym}${abs})` : `${sym}${abs}`;
}

function BalanceSheet({ data, sym, language, isNL, entityId, year, onSaved }) {
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const lbl = (nl, en) => (isNL && language === 'nl') ? nl : en;

  const startEdit = () => {
    // Pre-fill edits with current displayed values
    const init = {};
    for (const grp of (data.asset_groups || [])) {
      for (const item of grp.items) init[item.code] = item.balance;
    }
    for (const grp of (data.liability_groups || [])) {
      for (const item of grp.items) init[item.code] = Math.abs(item.balance);
    }
    if (data.equity_group) {
      for (const item of data.equity_group.items) {
        if (item.code !== '9999') init[item.code] = Math.abs(item.balance);
      }
    }
    setEdits(init);
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setEdits({}); };

  const handleChange = (code, val) => {
    setEdits(prev => ({ ...prev, [code]: val === '' ? '' : parseFloat(val) || 0 }));
  };

  const saveEdits = async () => {
    setSaving(true);
    const adjustments = Object.entries(edits)
      .filter(([code, val]) => val !== '' && val !== undefined)
      .map(([code, val]) => ({ account_code: code, desired_balance: parseFloat(val) || 0 }));
    try {
      const res = await fetch(`${API_URL}/api/balans/${entityId}/adjust`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ adjustments, year }),
      });
      if (res.ok) {
        toast.success('Balans aangepast');
        setEditMode(false); setEdits({});
        onSaved();
      } else {
        toast.error('Fout bij opslaan');
      }
    } catch { toast.error('Fout bij opslaan'); }
    finally { setSaving(false); }
  };

  const renderRow = (item, isAbsDisplay) => {
    const displayVal = isAbsDisplay ? Math.abs(item.balance) : item.balance;
    const editVal = edits[item.code];
    const isChanged = editMode && editVal !== undefined && Math.abs(editVal - displayVal) > 0.005;
    const isEditable = editMode && item.code !== '9999';

    return (
      <tr key={item.code} className={`border-b border-dashed ${isChanged ? 'bg-amber-50' : ''}`}>
        <td className="py-1.5">
          <span className="text-muted-foreground mr-1 text-xs">{item.code}</span> {item.name}
        </td>
        <td className="text-right py-1.5 tabular-nums font-medium w-32">
          {isEditable ? (
            <Input
              type="number" step="0.01"
              value={editVal ?? ''}
              onChange={e => handleChange(item.code, e.target.value)}
              className="h-7 w-28 text-right text-sm ml-auto"
              data-testid={`edit-${item.code}`}
            />
          ) : (
            fmt(sym, displayVal)
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="mt-4">
      <div className="flex justify-end mb-3 gap-2">
        {!editMode ? (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-balans-btn">
            <Pencil className="w-4 h-4 mr-1" /> {lbl('Bewerken', 'Edit')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={cancelEdit} data-testid="cancel-edit-btn">
              <X className="w-4 h-4 mr-1" /> {lbl('Annuleren', 'Cancel')}
            </Button>
            <Button size="sm" className="btn-brand" onClick={saveEdits} disabled={saving} data-testid="save-balans-btn">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                : <Save className="w-4 h-4 mr-1" />}
              {lbl('Opslaan', 'Save')}
            </Button>
          </>
        )}
      </div>

      {editMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4 text-sm text-amber-800">
          {lbl(
            'Pas de gewenste saldi aan. Het systeem berekent automatisch de benodigde openingsbalans.',
            'Edit desired balances. The system automatically calculates the required opening balance.'
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" data-testid="bs-assets-title">{lbl('Activa', 'Assets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left py-2">{lbl('Rekening', 'Account')}</th>
                  <th className="text-right py-2 font-semibold">{lbl('Saldo', 'Balance')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.asset_groups || []).map(grp => (
                  <React.Fragment key={grp.key}>
                    <tr><td colSpan={2} className="pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{(language === 'nl' && isNL) ? grp.label : (grp.label_en || grp.label)}</td></tr>
                    {grp.items.map(a => renderRow(a, false))}
                    <tr className="font-medium border-t">
                      <td className="py-1 text-xs text-muted-foreground">{lbl('Totaal', 'Total')} {(language === 'nl' && isNL) ? grp.label : (grp.label_en || grp.label)}</td>
                      <td className="text-right py-1 tabular-nums text-xs">{fmt(sym, grp.total)}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold border-t-2">
                  <td className="py-2">{lbl('TOTAAL ACTIVA', 'TOTAL ASSETS')}</td>
                  <td className="text-right py-2 tabular-nums">{fmt(sym, data.total_assets)}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" data-testid="bs-passiva-title">{lbl('Passiva', 'Liabilities & Equity')}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left py-2">{lbl('Rekening', 'Account')}</th>
                  <th className="text-right py-2 font-semibold">{lbl('Saldo', 'Balance')}</th>
                </tr>
              </thead>
              <tbody>
                {data.equity_group && data.equity_group.items.length > 0 && (
                  <React.Fragment>
                    <tr><td colSpan={2} className="pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{(language === 'nl' && isNL) ? data.equity_group.label : (data.equity_group.label_en || data.equity_group.label)}</td></tr>
                    {data.equity_group.items.map(e => renderRow(e, true))}
                    <tr className="font-medium border-t">
                      <td className="py-1 text-xs text-muted-foreground">{lbl('Totaal Eigen vermogen', 'Total Equity')}</td>
                      <td className="text-right py-1 tabular-nums text-xs">{fmt(sym, data.equity_group.total)}</td>
                    </tr>
                  </React.Fragment>
                )}
                {(data.liability_groups || []).map(grp => (
                  <React.Fragment key={grp.key}>
                    <tr><td colSpan={2} className="pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{(language === 'nl' && isNL) ? grp.label : (grp.label_en || grp.label)}</td></tr>
                    {grp.items.map(l => renderRow(l, true))}
                    <tr className="font-medium border-t">
                      <td className="py-1 text-xs text-muted-foreground">{lbl('Totaal', 'Total')} {(language === 'nl' && isNL) ? grp.label : (grp.label_en || grp.label)}</td>
                      <td className="text-right py-1 tabular-nums text-xs">{fmt(sym, grp.total)}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold border-t-2">
                  <td className="py-2">{lbl('TOTAAL PASSIVA', 'TOTAL LIABILITIES & EQUITY')}</td>
                  <td className="text-right py-2 tabular-nums">{fmt(sym, Math.abs(data.total_liabilities_equity))}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ProfitLoss({ data, sym, language, isNL }) {
  const lbl = (sec) => (isNL && language === 'nl') ? sec.label : (sec.label_en || sec.label);
  const sections = data.sections || [];

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg" data-testid="pl-title">
          {(isNL && language === 'nl') ? `Winst- en Verliesrekening ${data.year}` : `Income Statement ${data.year}`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <tbody>
            {sections.map((sec, idx) => {
              if (sec.is_subtotal) {
                const isNetResult = sec.key === '_net_result';
                return (
                  <tr key={sec.key} className={`font-bold ${isNetResult ? 'border-t-2 text-base' : 'border-t border-b bg-muted/30'}`}>
                    <td className="py-2">{lbl(sec)}</td>
                    <td className={`text-right py-2 tabular-nums ${sec.total >= 0 ? 'text-[#6EC998]' : 'text-red-500'}`}>
                      {fmt(sym, sec.total)}
                    </td>
                  </tr>
                );
              }
              const isRevenue = sec.key === 'revenue';
              return (
                <React.Fragment key={sec.key}>
                  <tr>
                    <td colSpan={2} className={`${idx > 0 ? 'pt-4' : 'pt-1'} pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider`}>
                      {lbl(sec)}
                    </td>
                  </tr>
                  {(sec.items || []).map(item => (
                    <tr key={item.code} className="border-b border-dashed">
                      <td className="py-1.5"><span className="text-muted-foreground mr-1 text-xs">{item.code}</span> {item.name}</td>
                      <td className={`text-right py-1.5 tabular-nums font-medium ${isRevenue ? 'text-[#6EC998]' : ''}`}>
                        {fmt(sym, item.amount)}
                      </td>
                    </tr>
                  ))}
                  {(sec.items || []).length > 0 && (
                    <tr className="font-semibold border-t">
                      <td className="py-1 text-xs">{(isNL && language === 'nl') ? 'Totaal' : 'Total'} {lbl(sec)}</td>
                      <td className={`text-right py-1 tabular-nums text-xs ${isRevenue ? 'text-[#6EC998]' : ''}`}>
                        {fmt(sym, sec.total)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {sections.length === 0 && (
              <>
                <tr><td colSpan={2} className="pt-2 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{language === 'nl' ? 'Omzet' : 'Revenue'}</td></tr>
                {(data.revenue || []).map(r => (
                  <tr key={r.code} className="border-b border-dashed">
                    <td className="py-2"><span className="text-muted-foreground mr-1">{r.code}</span> {r.name}</td>
                    <td className="text-right py-2 tabular-nums font-medium text-[#6EC998]">{fmt(sym, r.amount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold border-t"><td className="py-2">{language === 'nl' ? 'Totaal Omzet' : 'Total Revenue'}</td><td className="text-right py-2 tabular-nums text-[#6EC998]">{fmt(sym, data.total_revenue)}</td></tr>
                <tr><td colSpan={2} className="pt-6 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{language === 'nl' ? 'Kosten' : 'Expenses'}</td></tr>
                {(data.expenses || []).map(e => (
                  <tr key={e.code} className="border-b border-dashed">
                    <td className="py-2"><span className="text-muted-foreground mr-1">{e.code}</span> {e.name}</td>
                    <td className="text-right py-2 tabular-nums">{fmt(sym, e.amount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold border-t"><td className="py-2">{language === 'nl' ? 'Totaal Kosten' : 'Total Expenses'}</td><td className="text-right py-2 tabular-nums">{fmt(sym, data.total_expenses)}</td></tr>
                <tr className="font-bold border-t-2 text-lg">
                  <td className="py-3">{language === 'nl' ? 'Resultaat' : 'Net Result'}</td>
                  <td className={`text-right py-3 tabular-nums ${data.net_result >= 0 ? 'text-[#6EC998]' : 'text-red-500'}`}>{fmt(sym, data.net_result)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
